import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
} from "@mui/icons-material";
import { setupBusinessAccount } from "../../../services/settings.service";
import useAuthUser from "../../hooks/useAuthUser";
import { UserSettings } from "../../../models/settings.model";
import parsePhoneNumberFromString from "libphonenumber-js";
import BusinessSettingsContext from "../../Context/Settings/BusinessSettingsContext";

declare global {
  interface Window {
    fbAsyncInit?: () => void;
    FB: any;
    _meraaiSignup?: {
      waba_id?: string;
      phone_number_id?: string;
    };
  }
}

const CONFIG_ID = process.env.REACT_APP_FB_CONFIG_ID;

// --- helpers ---
type EmbeddedSignupMessage = {
  type?: string;
  event?: string;
  data?: {
    waba_id?: string;
    phone_number_id?: string;
    [k: string]: any;
  };
  [k: string]: any;
};

function safeParseMessage(data: any): EmbeddedSignupMessage | null {
  // Meta can send stringified JSON OR an object
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (typeof data === "object" && data !== null)
    return data as EmbeddedSignupMessage;
  return null;
}

function waitForSignupIds(
  getIds: () => { waba_id?: string; phone_number_id?: string },
  timeoutMs = 8000,
  intervalMs = 250,
): Promise<{ waba_id: string; phone_number_id: string }> {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const tick = () => {
      const { waba_id, phone_number_id } = getIds();
      if (waba_id && phone_number_id) {
        resolve({ waba_id, phone_number_id });
        return;
      }
      if (Date.now() - start > timeoutMs) {
        reject(
          new Error(
            "Timed out waiting for waba_id / phone_number_id from Embedded Signup",
          ),
        );
        return;
      }
      setTimeout(tick, intervalMs);
    };

    tick();
  });
}

export const WhatsAppEmbeddedSignup: React.FC = () => {
  const { settings: existingBusinessSettings, refreshSettings } = useContext(
    BusinessSettingsContext,
  );
  const theme = useTheme();
  const { user } = useAuthUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState<"idle" | "connected" | "error">("idle");
  const [lastEvent, setLastEvent] = useState<any>(null);
  const [businessDetails, setBusinessDetails] = useState<{
    phoneNumber?: string;
    wabaId?: string;
    displayName?: string;
    phoneNumberId?: string;
  } | null>(null);

  // Keep latest signup ids here (avoids state timing issues)
  const signupRef = useRef<{
    waba_id?: string;
    phone_number_id?: string;
    eventType?: string;
  }>({});

  useEffect(() => {
    try {
      if (existingBusinessSettings) {
        const { name, fullPhoneNumber } = existingBusinessSettings;

        if (fullPhoneNumber) {
          const parsedPhoneNumber = parsePhoneNumberFromString(fullPhoneNumber);

          setBusinessDetails({
            phoneNumber: parsedPhoneNumber
              ? `+${
                  parsedPhoneNumber.countryCallingCode
                } ${parsedPhoneNumber.formatNational()}`
              : fullPhoneNumber,
            // NOTE: This is not a real WABA id; keeping your existing behavior
            wabaId: fullPhoneNumber.replaceAll("+", "").replace(/[^0-9]/g, ""),
            displayName: name ?? "",
          });

          setConnecting(false);
          setStatus("connected");
        } else {
          throw new Error("No existing phonenumber found in business settings");
        }
      } else {
        throw new Error("No existing business settings found");
      }
    } catch (e) {
      console.log(e);
    }
  }, [existingBusinessSettings]);

  // Detect if FB SDK already available
  useEffect(() => {
    if (typeof window === "undefined") return;

    const check = () => {
      if (window.FB) {
        setSdkLoaded(true);
        return true;
      }
      return false;
    };

    if (check()) return;

    // If your app loads SDK elsewhere, we just poll briefly.
    const t = setInterval(() => {
      if (check()) clearInterval(t);
    }, 300);

    const kill = setTimeout(() => {
      clearInterval(t);
      if (!window.FB)
        setSdkError(
          "Facebook SDK not loaded. Ensure the FB SDK script is included.",
        );
    }, 7000);

    return () => {
      clearInterval(t);
      clearTimeout(kill);
    };
  }, []);

  // Listen for WA_EMBEDDED_SIGNUP session info (waba_id, phone_number_id, etc.)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event: MessageEvent) => {
      // Allow only FB origins
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }

      const msg = safeParseMessage(event.data);
      if (!msg || msg.type !== "WA_EMBEDDED_SIGNUP") return;

      setLastEvent(msg);

      const eventName = msg.event || "";
      if (eventName) signupRef.current.eventType = eventName;
      const waba_id = msg.data?.waba_id;
      const phone_number_id = msg.data?.phone_number_id;

      // ✅ generalized FINISH detection
      const isFinishEvent = eventName.startsWith("FINISH");

      // Store ids whenever we receive them (even before FINISH)
      if (waba_id) signupRef.current.waba_id = waba_id;
      if (phone_number_id) signupRef.current.phone_number_id = phone_number_id;

      if (signupRef.current.waba_id || signupRef.current.phone_number_id) {
        window._meraaiSignup = {
          waba_id: signupRef.current.waba_id,
          phone_number_id: signupRef.current.phone_number_id,
        };
      }

      if (isFinishEvent) {
      }

      // Optional: handle cancel/error
      const isCancel = eventName.startsWith("CANCEL");
      const isError =
        eventName.startsWith("ERROR") || eventName.includes("FAIL");
      if (isCancel || isError) {
        console.warn("[WhatsApp SDK] Embedded signup cancelled/failed:", msg);
        setStatus("error");
        setConnecting(false);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Called by FB.login() when user finishes/cancels the popup
  const postFbLogin = async (response: any) => {
    if (!response?.authResponse) {
      setConnecting(false);
      setStatus("error");
      return;
    }

    const code = response.authResponse.code;

    try {
      // ✅ wait for waba_id + phone_number_id if they arrive slightly after FB.login callback
      const { waba_id, phone_number_id } = await waitForSignupIds(
        () => ({
          waba_id: signupRef.current.waba_id || window._meraaiSignup?.waba_id,
          phone_number_id:
            signupRef.current.phone_number_id ||
            window._meraaiSignup?.phone_number_id,
        }),
        8000,
      );

      console.log(
        "[WhatsApp SDK] Calling backend API to store token and details",
        {
          waba_id,
          phone_number_id,
        },
      );

      const settingsResp: UserSettings = await setupBusinessAccount(
        user?.uid as string,
        waba_id,
        phone_number_id,
        signupRef.current.eventType!,
      );

      const phoneNumber = parsePhoneNumberFromString(
        settingsResp.businessSettings.fullPhoneNumber,
      );

      setBusinessDetails({
        phoneNumber:
          phoneNumber?.number ?? settingsResp.businessSettings.fullPhoneNumber,
        wabaId: waba_id,
        displayName: settingsResp.businessSettings.name,
        phoneNumberId: phone_number_id,
      });

      setStatus("connected");
      refreshSettings(settingsResp?.uuid as string);
    } catch (e: any) {
      console.error(e);
      setStatus("error");
      alert(
        e?.message ||
          "Unable to connect to business account. Please close the popup and try again.",
      );
    } finally {
      setConnecting(false);
    }
  };

  const fbLoginCallback = (response: any) => postFbLogin(response);

  // Launch the Embedded Signup + QR popup
  const launchSignup = () => {
    if (!window.FB) {
      console.warn("[WhatsApp SDK] FB not ready yet");
      setSdkError("Facebook SDK not loaded. Please try again.");
      return;
    }

    // clear previous ids
    signupRef.current = {};
    window._meraaiSignup = {};

    setConnecting(true);
    setStatus("idle");
    setSdkError(null);

    window.FB.login(fbLoginCallback, {
      config_id: CONFIG_ID,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: "whatsapp_business_app_onboarding",
        sessionInfoVersion: "3",
      },
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        px: 0,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 720,
          borderRadius: 3,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          <Stack spacing={3}>
            {/* Header Section */}
            <Box>
              <Stack
                direction="row"
                spacing={2.5}
                alignItems="flex-start"
                sx={{ mb: 2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    backgroundColor:
                      status === "connected"
                        ? "rgba(76, 175, 80, 0.15)"
                        : "rgba(37, 211, 102, 0.15)",
                    flexShrink: 0,
                  }}
                >
                  <WhatsAppIcon
                    sx={{
                      color:
                        status === "connected"
                          ? theme.palette.success.main
                          : "#25D366",
                      fontSize: 32,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ mb: 0.5, lineHeight: 1.2 }}
                      >
                        WhatsApp Integration
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.4 }}
                      >
                        {status === "connected"
                          ? "Your WhatsApp Business Account is connected"
                          : "Connect your WhatsApp Business Account to start messaging"}
                      </Typography>
                    </Box>

                    {status === "connected" && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 2,
                          py: 0.75,
                          borderRadius: "12px",
                          backgroundColor: theme.palette.success.main,
                          flexShrink: 0,
                        }}
                      >
                        <CheckCircleIcon sx={{ color: "#fff", fontSize: 18 }} />
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: "#fff",
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Active
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>

            {/* Connected Business Details */}
            {status === "connected" && businessDetails && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography
                    variant="overline"
                    fontWeight={700}
                    sx={{
                      mb: 2,
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Account Details
                  </Typography>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: "12px",
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "rgba(76, 175, 80, 0.08)"
                            : "rgba(76, 175, 80, 0.12)",
                        border: `2px solid ${theme.palette.success.main}30`,
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        fontWeight={700}
                        sx={{
                          mb: 1,
                          color: theme.palette.success.main,
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Business Name
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          color: theme.palette.text.primary,
                          wordBreak: "break-word",
                        }}
                      >
                        {businessDetails.displayName || "Not specified"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.action.hover,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        fontWeight={700}
                        sx={{
                          mb: 1,
                          color: "text.secondary",
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Phone Number
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          fontFamily: "monospace",
                          color: theme.palette.primary.main,
                        }}
                      >
                        {businessDetails.phoneNumber || "N/A"}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </>
            )}

            {/* How it works - Before Connection */}
            {status !== "connected" && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography
                    variant="overline"
                    fontWeight={700}
                    sx={{
                      mb: 2.5,
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    How It Works
                  </Typography>
                  <Stack alignItems={"center"}>
                    <Stack spacing={2}>
                      {[
                        {
                          title: "Click Connect Button",
                          desc: "Start the secure connection process",
                        },
                        {
                          title: "Sign In with Facebook",
                          desc: "Use your Facebook account to authenticate",
                        },
                        {
                          title: "Select Your WhatsApp Number",
                          desc: "Choose your business WhatsApp number",
                        },
                        {
                          title: "Scan QR Code",
                          desc: "Scan with your WhatsApp app to verify",
                        },
                      ].map((s, idx) => (
                        <Stack
                          key={idx}
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          justifyContent={"flex-start"}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: 32,
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              backgroundColor: theme.palette.success.main,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              flexShrink: 0,
                              mt: 0.25,
                            }}
                          >
                            {idx + 1}
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ mb: 0.25 }}
                            >
                              {s.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {s.desc}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </>
            )}

            {/* Status Messages */}
            {!sdkLoaded && !sdkError && (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                  border: `1px solid ${theme.palette.info.main}30`,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" fontWeight={500}>
                    Loading WhatsApp SDK…
                  </Typography>
                </Stack>
              </Alert>
            )}

            {sdkError && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                  borderLeft: `4px solid ${theme.palette.error.main}`,
                  border: `1px solid ${theme.palette.error.main}30`,
                }}
                icon={<ErrorIcon sx={{ fontSize: 22 }} />}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={700}>
                    Connection Error
                  </Typography>
                  <Typography variant="caption">{sdkError}</Typography>
                </Stack>
              </Alert>
            )}

            {status === "error" && (
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 2,
                  backgroundColor: "rgba(251, 140, 0, 0.08)",
                  borderLeft: `4px solid ${theme.palette.warning.main}`,
                  border: `1px solid ${theme.palette.warning.main}30`,
                }}
                icon={<ErrorIcon sx={{ fontSize: 22 }} />}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={700}>
                    Connection Failed
                  </Typography>
                  <Typography variant="caption">
                    Something went wrong. Please close the popup and try again.
                  </Typography>
                </Stack>
              </Alert>
            )}

            {/* Action Button - Only show if not connected */}
            {status !== "connected" && (
              <Button
                fullWidth
                variant="contained"
                size={isMobile ? "small" : "medium"}
                onClick={launchSignup}
                disabled={!sdkLoaded || !!sdkError || connecting}
                startIcon={
                  connecting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <FacebookIcon sx={{ fontSize: 22 }} />
                  )
                }
                sx={{
                  backgroundColor: "#1877f2",
                  py: isMobile ? 1.25 : 1.75,
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  letterSpacing: "0.3px",
                  borderRadius: "12px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#0A66C2",
                    transform: "none",
                    boxShadow: "none",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.text.disabled,
                    transform: "none",
                  },
                }}
              >
                {connecting ? "Opening Facebook…" : "Connect WhatsApp Account"}
              </Button>
            )}

            {/* Debug Info - Development Only */}
            {process.env.NODE_ENV === "development" && lastEvent && (
              <>
                <Divider />
                <details style={{ cursor: "pointer" }}>
                  <summary style={{ userSelect: "none" }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        textTransform: "uppercase",
                        color: "text.secondary",
                      }}
                    >
                      Debug Session Info (Dev Only)
                    </Typography>
                  </summary>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: "0.75rem",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      marginTop: 1,
                      maxHeight: 200,
                      overflow: "auto",
                      backgroundColor: "action.hover",
                      padding: 1.5,
                      borderRadius: 1,
                      fontFamily: "monospace",
                    }}
                  >
                    {JSON.stringify(lastEvent, null, 2)}
                  </Box>
                </details>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WhatsAppEmbeddedSignup;
